/**
 * Planos e limites do Otto (docs/specs/otto/TDD.md §12 e PRICING.md §6).
 *
 * Os limites do Otto são **chaves de plano** no mesmo catálogo que o resto da plataforma usa (`features.js`), com prefixo
 * `otto.` e categoria `otto`. Valor 0 numa chave booleana ou numérica = recurso travado.
 *
 * **Regra para quem aplica um limite: ler de forma estrutural** (a partir de `getAccountFeatures`, com `readNumericLimit`
 * e `readBooleanLimit`), **nunca por `checkFeature`**. Em trial, `checkFeature` libera todas as chaves: o Gratuito em trial
 * gravaria sem limite.
 *
 * Os modelos de plano abaixo (`OTTO_PLAN_TEMPLATES`) NÃO são semeados em produção: os nomes ainda são provisórios. Um
 * script explícito (`scripts/seed-otto-plans.mjs`) grava os três quando o produto mandar.
 */

/** Categoria do catálogo (a tela de planos lê as categorias do servidor). */
export const OTTO_CATEGORY = Object.freeze({ label: 'Otto', icon: 'Video', order: 18 });

/** As chaves, em um só lugar, para ninguém digitar o texto solto. */
export const OTTO_KEYS = Object.freeze({
  VIDEO_QUALITY_MAX: 'otto.video_quality_max',
  ROOM_PARTICIPANTS_MAX: 'otto.room_participants_max',
  ROOM_CAMERAS_MAX: 'otto.room_cameras_max',
  MEETING_MAX_MINUTES: 'otto.meeting_max_minutes',
  RECORDINGS_MONTHLY: 'otto.recordings_monthly',
  RETENTION_DAYS: 'otto.retention_days',
  ANALYSIS_MONTHLY: 'otto.analysis_monthly',
  PLAYBOOKS_MAX: 'otto.playbooks_max',
  CALENDAR_ACCOUNTS_MAX: 'otto.calendar_accounts_max',
  API_REQUESTS_MONTHLY: 'otto.api_requests_monthly',
  ASK_MONTHLY: 'otto.ask_monthly',
  FOLLOWUP_DRAFT: 'otto.followup_draft',
  SHARE_EXPORT: 'otto.share_export',
  RECORDING_DOWNLOAD: 'otto.recording_download',
  CALL_SEARCH: 'otto.call_search',
  ASK_ACROSS_CALLS: 'otto.ask_across_calls',
  WEEKLY_REPORT: 'otto.weekly_report',
  CUSTOM_VOCABULARY: 'otto.custom_vocabulary',
  CUSTOM_BRANDING: 'otto.custom_branding',
  PRIORITY_SUPPORT: 'otto.priority_support',
});

/**
 * Chaves que todo plano Otto inclui **por definição**: o Otto é a própria chamada de vídeo com transcrição. Quem decide é o
 * produto do plano (`Plan.product = OTTO`), não o superadmin ligando chave por chave. `getAccountFeatures` as devolve
 * ligadas para esses planos (a API e a web leem o mesmo resultado) e a tela de planos mostra "Incluído no Otto".
 *
 * Num plano da PLATAFORMA elas seguem sendo as chaves de sempre: ligar `calling.video` (e `calling.video_transcription`) no
 * plano libera o vídeo para todos os usuários da conta com a permissão do perfil; deixar desligadas e usar as licenças
 * incluídas (`otto.included_*`) dá o Otto só a quem o administrador atribuir.
 *
 * Os limites de uso (`calling.video_minutes_monthly`, `calling.video_unlimited`) NÃO entram aqui: são cota, e o plano decide.
 */
export const OTTO_IMPLIED_FEATURE_KEYS = Object.freeze(['calling.video', 'calling.video_transcription']);

const number = (key, label, description, unit) => ({
  key, label, category: 'otto', defaultLimitType: 'number', description, unit, addonPrice: null,
});
const flag = (key, label, description) => ({
  key, label, category: 'otto', defaultLimitType: 'boolean', description, addonPrice: null,
});

/**
 * Entradas do catálogo. `unit` é o texto que a tela de planos mostra ao lado do número (min, dias, px...). Nenhuma tem
 * preço self-service: os planos do Otto são contratados como plano, não como addon.
 */
export const OTTO_FEATURE_KEYS = [
  number(OTTO_KEYS.VIDEO_QUALITY_MAX, 'Qualidade máxima de vídeo', 'Altura máxima do vídeo publicado (540, 720 ou 1080). Aplicada só no cliente', 'px'),
  number(OTTO_KEYS.ROOM_PARTICIPANTS_MAX, 'Pessoas por sala', 'Máximo de pessoas na mesma sala. Congelado na criação da chamada', 'pessoas'),
  number(OTTO_KEYS.ROOM_CAMERAS_MAX, 'Câmeras ligadas ao mesmo tempo', 'Máximo de câmeras ligadas por sala. Protege as salas grandes', 'câmeras'),
  number(OTTO_KEYS.MEETING_MAX_MINUTES, 'Duração máxima da reunião', 'A reunião avisa 5 minutos antes e encerra ao chegar no limite. Congelado na criação', 'min'),
  number(OTTO_KEYS.RECORDINGS_MONTHLY, 'Gravações por mês', 'Quantidade de gravações iniciadas por mês, somada na conta', 'por mês'),
  number(OTTO_KEYS.RETENTION_DAYS, 'Guarda de gravações e transcrições', 'Quanto tempo a gravação e a transcrição ficam guardadas. Nunca vem de um experimento', 'dias'),
  number(OTTO_KEYS.ANALYSIS_MONTHLY, 'Análises por mês', 'Análise de sentimento e de playbook por mês, somada na conta (0 = travado)', 'por mês'),
  number(OTTO_KEYS.PLAYBOOKS_MAX, 'Playbooks ativos', 'Quantidade de playbooks ativos (0 = travado)', 'playbooks'),
  number(OTTO_KEYS.CALENDAR_ACCOUNTS_MAX, 'Contas de calendário', 'Contas de Google Calendar e Outlook conectadas (0 = travado)', 'contas'),
  number(OTTO_KEYS.API_REQUESTS_MONTHLY, 'Requisições de API por mês', 'Requisições da API e dos webhooks por mês (0 = travado)', 'por mês'),
  number(OTTO_KEYS.ASK_MONTHLY, 'Perguntas sobre a chamada por mês', 'Perguntas ao assistente sobre uma chamada, por mês', 'por mês'),
  flag(OTTO_KEYS.FOLLOWUP_DRAFT, 'Mensagem de acompanhamento', 'Rascunho de mensagem de acompanhamento depois da chamada'),
  flag(OTTO_KEYS.SHARE_EXPORT, 'Compartilhar e exportar', 'Compartilhar por link e exportar a chamada'),
  flag(OTTO_KEYS.RECORDING_DOWNLOAD, 'Baixar a gravação', 'Baixar o arquivo da gravação'),
  flag(OTTO_KEYS.CALL_SEARCH, 'Busca entre chamadas', 'Buscar no conteúdo das chamadas'),
  flag(OTTO_KEYS.ASK_ACROSS_CALLS, 'Perguntar entre várias chamadas', 'Perguntas que consideram várias chamadas'),
  flag(OTTO_KEYS.WEEKLY_REPORT, 'Relatório semanal da equipe', 'Relatório semanal por e-mail e relatórios agendados'),
  flag(OTTO_KEYS.CUSTOM_VOCABULARY, 'Vocabulário personalizado', 'Termos próprios (por exemplo, jurídicos) para a transcrição'),
  flag(OTTO_KEYS.CUSTOM_BRANDING, 'Marca própria', 'Marca própria na sala e na página do convidado'),
  flag(OTTO_KEYS.PRIORITY_SUPPORT, 'Suporte prioritário', 'Atendimento prioritário do suporte'),
];

/**
 * Licenças do Otto que o plano de uma conta da PLATAFORMA inclui, sem custo (docs/specs/otto/PLATFORM_ADDON.md). Uma chave por
 * plano Otto que o administrador pode dar a cada usuário. Valem no plano da plataforma e, por conta, em `customLimits`. Ficam
 * FORA de `OTTO_KEYS` de propósito: os planos Otto não as definem (um plano Otto já é por licença), só os da plataforma.
 */
export const OTTO_INCLUDED_KEYS = Object.freeze({
  FREE: 'otto.included_free',
  PRO: 'otto.included_pro',
  ADVANCED: 'otto.included_advanced',
});

export const OTTO_INCLUDED_FEATURE_KEYS = [
  number(OTTO_INCLUDED_KEYS.FREE, 'Licenças Otto Gratuito incluídas', 'Quantos usuários da conta podem usar o Otto Gratuito. É o teto: o Gratuito não se compra (padrão 1)', 'licenças'),
  number(OTTO_INCLUDED_KEYS.PRO, 'Licenças Otto Pro incluídas', 'Licenças do Otto Pro que vêm no plano sem custo. O que passar disto é cobrado (padrão 0)', 'licenças'),
  number(OTTO_INCLUDED_KEYS.ADVANCED, 'Licenças Otto Avançado incluídas', 'Licenças do Otto Avançado que vêm no plano sem custo. O que passar disto é cobrado (padrão 0)', 'licenças'),
];

const n = (limitValue) => ({ limitType: 'number', limitValue });
const on = { limitType: 'boolean', limitValue: 1 };
const off = { limitType: 'boolean', limitValue: 0 };
const unlimited = { limitType: 'unlimited', limitValue: null };

/**
 * Os três planos do PRICING.md §6 (nomes provisórios). Preço por **licença** (atribuída pelo administrador a cada usuário). `yearlyPrice` é o valor
 * do ano inteiro (o ciclo anual cobra `yearlyPrice / 12` por mês, 20% abaixo do mensal). `trialDays` é 0: o Otto usa o
 * experimento por funcionalidade, não o trial do plano (TDD §13).
 */
export const OTTO_PLAN_TEMPLATES = Object.freeze({
  free: {
    name: 'Gratuito', slug: 'otto-gratuito', description: 'Otto: chamadas de vídeo para começar',
    monthlyPrice: 0, yearlyPrice: 0, type: 'free', displayOrder: 1,
    features: {
      // Em plano por licença `users.max` é o teto de licenças (services/licenses/licenseLimit.js): o Gratuito é de 1 pessoa
      'users.max': n(1),
      'calling.video': on,
      'calling.video_minutes_monthly': n(300),
      'calling.video_transcription': on,
      [OTTO_KEYS.VIDEO_QUALITY_MAX]: n(540),
      [OTTO_KEYS.ROOM_PARTICIPANTS_MAX]: n(5),
      [OTTO_KEYS.ROOM_CAMERAS_MAX]: n(5),
      [OTTO_KEYS.MEETING_MAX_MINUTES]: n(45),
      [OTTO_KEYS.RECORDINGS_MONTHLY]: n(5),
      [OTTO_KEYS.RETENTION_DAYS]: n(45),
      [OTTO_KEYS.ANALYSIS_MONTHLY]: n(0),
      [OTTO_KEYS.PLAYBOOKS_MAX]: n(0),
      [OTTO_KEYS.CALENDAR_ACCOUNTS_MAX]: n(0),
      [OTTO_KEYS.API_REQUESTS_MONTHLY]: n(0),
      [OTTO_KEYS.ASK_MONTHLY]: n(5),
      [OTTO_KEYS.FOLLOWUP_DRAFT]: off,
      [OTTO_KEYS.SHARE_EXPORT]: off,
      [OTTO_KEYS.RECORDING_DOWNLOAD]: off,
      [OTTO_KEYS.CALL_SEARCH]: off,
      [OTTO_KEYS.ASK_ACROSS_CALLS]: off,
      [OTTO_KEYS.WEEKLY_REPORT]: off,
      [OTTO_KEYS.CUSTOM_VOCABULARY]: off,
      [OTTO_KEYS.CUSTOM_BRANDING]: off,
      [OTTO_KEYS.PRIORITY_SUPPORT]: off,
    },
  },
  pro: {
    name: 'Pro', slug: 'otto-pro', description: 'Otto: HD, gravações, análises e integrações',
    monthlyPrice: 169, yearlyPrice: 1620, type: 'paid', displayOrder: 2,
    features: {
      'users.max': unlimited,
      'calling.video': on,
      'calling.video_minutes_monthly': n(2400),
      'calling.video_transcription': on,
      [OTTO_KEYS.VIDEO_QUALITY_MAX]: n(720),
      [OTTO_KEYS.ROOM_PARTICIPANTS_MAX]: n(25),
      [OTTO_KEYS.ROOM_CAMERAS_MAX]: n(10),
      [OTTO_KEYS.MEETING_MAX_MINUTES]: n(180),
      [OTTO_KEYS.RECORDINGS_MONTHLY]: n(30),
      [OTTO_KEYS.RETENTION_DAYS]: n(180),
      [OTTO_KEYS.ANALYSIS_MONTHLY]: n(30),
      [OTTO_KEYS.PLAYBOOKS_MAX]: n(1),
      [OTTO_KEYS.CALENDAR_ACCOUNTS_MAX]: n(2),
      [OTTO_KEYS.API_REQUESTS_MONTHLY]: n(10000),
      [OTTO_KEYS.ASK_MONTHLY]: unlimited,
      [OTTO_KEYS.FOLLOWUP_DRAFT]: on,
      [OTTO_KEYS.SHARE_EXPORT]: on,
      [OTTO_KEYS.RECORDING_DOWNLOAD]: on,
      [OTTO_KEYS.CALL_SEARCH]: on,
      [OTTO_KEYS.ASK_ACROSS_CALLS]: off,
      [OTTO_KEYS.WEEKLY_REPORT]: off,
      [OTTO_KEYS.CUSTOM_VOCABULARY]: off,
      [OTTO_KEYS.CUSTOM_BRANDING]: off,
      [OTTO_KEYS.PRIORITY_SUPPORT]: off,
    },
  },
  advanced: {
    name: 'Avançado', slug: 'otto-avancado', description: 'Otto: 100 pessoas por sala, Full HD e relatórios da equipe',
    monthlyPrice: 399, yearlyPrice: 3828, type: 'paid', displayOrder: 3,
    features: {
      'users.max': unlimited,
      'calling.video': on,
      'calling.video_minutes_monthly': n(6000),
      'calling.video_transcription': on,
      [OTTO_KEYS.VIDEO_QUALITY_MAX]: n(1080),
      [OTTO_KEYS.ROOM_PARTICIPANTS_MAX]: n(100),
      [OTTO_KEYS.ROOM_CAMERAS_MAX]: n(10),
      [OTTO_KEYS.MEETING_MAX_MINUTES]: n(480),
      [OTTO_KEYS.RECORDINGS_MONTHLY]: n(80),
      [OTTO_KEYS.RETENTION_DAYS]: n(365),
      [OTTO_KEYS.ANALYSIS_MONTHLY]: n(80),
      [OTTO_KEYS.PLAYBOOKS_MAX]: n(10),
      [OTTO_KEYS.CALENDAR_ACCOUNTS_MAX]: n(5),
      [OTTO_KEYS.API_REQUESTS_MONTHLY]: n(100000),
      [OTTO_KEYS.ASK_MONTHLY]: unlimited,
      [OTTO_KEYS.FOLLOWUP_DRAFT]: on,
      [OTTO_KEYS.SHARE_EXPORT]: on,
      [OTTO_KEYS.RECORDING_DOWNLOAD]: on,
      [OTTO_KEYS.CALL_SEARCH]: on,
      [OTTO_KEYS.ASK_ACROSS_CALLS]: on,
      [OTTO_KEYS.WEEKLY_REPORT]: on,
      [OTTO_KEYS.CUSTOM_VOCABULARY]: on,
      [OTTO_KEYS.CUSTOM_BRANDING]: on,
      [OTTO_KEYS.PRIORITY_SUPPORT]: on,
    },
  },
});

/**
 * Os três planos como registros prontos para o banco: `plan` (colunas de `Plan`) e `features` (linhas de `PlanFeature`).
 * Plano do Otto: produto OTTO, cobrado por licença, sem trial de plano (o experimento por funcionalidade o substitui) e
 * fora da página de preços até o produto decidir (`isPublic: false`).
 */
export function buildOttoPlanRecords() {
  return Object.values(OTTO_PLAN_TEMPLATES).map((template) => ({
    plan: {
      name: template.name,
      slug: template.slug,
      description: template.description,
      monthlyPrice: template.monthlyPrice,
      yearlyPrice: template.yearlyPrice,
      type: template.type,
      trialDays: 0,
      product: 'OTTO',
      billingUnit: 'SEAT',
      isActive: true,
      isPublic: false,
      displayOrder: template.displayOrder,
    },
    features: Object.entries(template.features).map(([featureKey, { limitType, limitValue }]) => ({
      featureKey,
      limitType,
      limitValue,
    })),
  }));
}

/**
 * Teto da plataforma para os limites de sala: nenhum plano passa disto, mesmo que o superadmin digite mais. Vem do que a
 * infraestrutura de mídia aguenta (100 pessoas por sala e 10 câmeras, PRICING §9) e da maior reunião vendida (8 horas).
 */
export const ROOM_LIMIT_CEILINGS = Object.freeze({ maxParticipants: 100, maxCameras: 10, maxDurationMinutes: 480 });

/**
 * Os limites do plano que ficam CONGELADOS na chamada, no momento em que ela é criada (uma chamada em andamento não muda
 * se a conta mudar de plano). Leitura estrutural de `getAccountFeatures().features`. Chave ausente, zero ou inválida vira
 * `null`: o código usa o padrão de sempre (conta sem Otto não muda de comportamento). Ilimitado vale o teto da plataforma.
 *
 * @param {Record<string, {limitType: string, limitValue: unknown}> | undefined | null} features
 * @returns {{ maxParticipants: number|null, maxCameras: number|null, maxDurationMinutes: number|null, recordingRetentionDays: number|null }}
 */
export function resolveRoomLimits(features) {
  const capped = (key, ceiling) => {
    const value = readNumericLimit(features, key, null);
    if (value === null || !(value > 0)) return null;
    return Math.min(value === Infinity ? ceiling : Math.floor(value), ceiling);
  };
  const retention = readNumericLimit(features, OTTO_KEYS.RETENTION_DAYS, null);
  return {
    maxParticipants: capped(OTTO_KEYS.ROOM_PARTICIPANTS_MAX, ROOM_LIMIT_CEILINGS.maxParticipants),
    maxCameras: capped(OTTO_KEYS.ROOM_CAMERAS_MAX, ROOM_LIMIT_CEILINGS.maxCameras),
    maxDurationMinutes: capped(OTTO_KEYS.MEETING_MAX_MINUTES, ROOM_LIMIT_CEILINGS.maxDurationMinutes),
    recordingRetentionDays: retention !== null && Number.isFinite(retention) && retention > 0 ? Math.floor(retention) : null,
  };
}

/**
 * Os planos Otto que uma conta da plataforma pode dar a cada usuário. O plano é identificado pelo slug dos modelos
 * (`scripts/seed-otto-plans.mjs`), e cada um tem a chave das licenças incluídas e o padrão quando a chave não existe: o
 * Gratuito vale 1 (como o teto das contas Otto) e os pagos 0 (tudo que for atribuído é cobrado).
 */
export const OTTO_TIERS = Object.freeze([
  Object.freeze({ tier: 'free', slug: OTTO_PLAN_TEMPLATES.free.slug, includedKey: OTTO_INCLUDED_KEYS.FREE, defaultIncluded: 1, paid: false }),
  Object.freeze({ tier: 'pro', slug: OTTO_PLAN_TEMPLATES.pro.slug, includedKey: OTTO_INCLUDED_KEYS.PRO, defaultIncluded: 0, paid: true }),
  Object.freeze({ tier: 'advanced', slug: OTTO_PLAN_TEMPLATES.advanced.slug, includedKey: OTTO_INCLUDED_KEYS.ADVANCED, defaultIncluded: 0, paid: true }),
]);

/**
 * Quantas licenças de cada plano Otto a conta tem incluídas, sem custo. Lê a chave de cada plano (do mapa `features` de
 * `getAccountFeatures`, que já traz a sobrescrita por conta); ausente vale o padrão (Gratuito 1, Pro e Avançado 0); ilimitado
 * vale `Infinity`; número inválido ou negativo vale o padrão. Usada pela atribuição (teto do Gratuito) e pela cobrança.
 *
 * @param {Record<string, {limitType: string, limitValue: unknown}> | null | undefined} features
 * @returns {Record<'free' | 'pro' | 'advanced', number>}
 */
export function resolveOttoIncluded(features) {
  const included = {};
  for (const { tier, includedKey, defaultIncluded } of OTTO_TIERS) {
    const value = readNumericLimit(features, includedKey, null);
    if (value === null || value < 0) included[tier] = defaultIncluded;
    else included[tier] = Number.isFinite(value) ? Math.floor(value) : Infinity;
  }
  return included;
}

/** @param {string | null | undefined} slug @returns {typeof OTTO_TIERS[number] | null} */
export const ottoTierBySlug = (slug) => OTTO_TIERS.find((t) => t.slug === slug) ?? null;

/**
 * Lê um limite numérico do mapa `features` de `getAccountFeatures`.
 * @param {Record<string, {limitType: string, limitValue: unknown}> | undefined | null} features
 * @param {string} key
 * @param {number | null} [fallback=null] valor quando a chave não existe no plano (conta sem Otto: vale o padrão do código)
 * @returns {number | null} `Infinity` para ilimitado
 */
export function readNumericLimit(features, key, fallback = null) {
  const entry = features?.[key];
  if (!entry) return fallback;
  if (entry.limitType === 'unlimited') return Infinity;
  if (entry.limitType === 'number') {
    const value = Number(entry.limitValue);
    return Number.isFinite(value) ? value : fallback;
  }
  return fallback;
}

/**
 * Lê um limite booleano do mapa `features`. Chave ausente = travado.
 * @returns {boolean}
 */
export function readBooleanLimit(features, key) {
  const entry = features?.[key];
  if (!entry) return false;
  if (entry.limitType === 'unlimited') return true;
  return entry.limitValue === true || Number(entry.limitValue) === 1;
}
